import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Center } from "@freightsimple/ui-core/PageLayout/Center";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import type { UUID } from "@/Shared/types";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import { useBookerNames } from "@/Hooks/useBookerNames";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import moment from "moment";
import type { ReactNode } from "react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShipmentService } from "@/Services/ShipmentService";
import ShipmentRow from "@/Shared/Shipment/CustomComponents/ShipmentRow";
import { BranchFilterOptionType } from "@/Shared/Types/BranchFilterOptionType";
import DashboardPhotoUploadReminderBanner from "./CustomComponents/DashboardPhotoUploadReminderBanner";
import { useDashboardPhotoUploadReminderDismissed } from "./Hooks/useDashboardPhotoUploadReminderDismissed";
import {
  UserSelectType,
  useDashboardUserSelect,
} from "@/Features/Dashboard/Exports/Hooks/useDashboardUserSelect";

function dashboardRowForShipment(
  shipment: Shipment,
  onClick: () => void,
  bookedByName: string | undefined,
) {
  const quote = shipment.selectedQuote;

  return (
    <ShipmentRow
      key={shipment.shipmentId}
      lineItems={shipment.lineItems}
      shipmentState={shipment.shipmentState}
      pickupLocation={shipment.pickupLocation}
      deliveryLocation={shipment.deliveryLocation}
      pickupDate={shipment.pickupDate}
      pickupHours={shipment.pickupLocation.hours}
      appointmentDate={shipment.appointmentDate}
      expectedDeliveryDate={shipment.expectedDeliveryDate}
      expectedDeliveryHours={shipment.expectedDeliveryHours}
      actualDeliveryDate={shipment.actualDeliveryDate}
      actualDeliveryTime={shipment.actualDeliveryTime}
      carrierIdentifier={quote?.carrierIdentifier}
      onClick={onClick}
      pickupReference={shipment.pickupReferenceNumber}
      deliveryReference={shipment.deliveryReferenceNumber}
      needsCustomsDocs={shipment.needsCustomsDocs}
      manualQuotingOpen={false}
      freightClaims={shipment.hasFreightClaims}
      bookedByName={bookedByName}
    />
  );
}

interface SectionForShipmentStateProps {
  sectionTitle: string;
  subtitle: ReactNode | undefined;
  shipments: Array<Shipment>;
  bookerNameFor: (bookedBy: string | undefined) => string | undefined;
}

function SectionForShipmentState_(props: SectionForShipmentStateProps) {
  const { sectionTitle, subtitle, shipments, bookerNameFor } = props;
  const navigate = useNavigate();

  function onClick(shipmentId: UUID) {
    const url = `/view-shipment?shipmentId=${shipmentId}`;
    navigate(url);
  }

  if (shipments.length === 0) {
    return <></>;
  }

  return (
    <Stack gap={2}>
      <Stack gap={0} align="start">
        <Heading level={2} size="2xl" weight="medium">
          {sectionTitle}
        </Heading>
        {subtitle && (
          <T size="sm" color="muted">
            {subtitle}
          </T>
        )}
      </Stack>
      {/* The rows carry their own bottom margin, so the list needs no gap. */}
      <Stack gap={0}>
        {shipments.map((s) => {
          return dashboardRowForShipment(
            s,
            function () {
              onClick(s.shipmentId);
            },
            bookerNameFor(s.bookedBy),
          );
        })}
      </Stack>
    </Stack>
  );
}

function AddShipmentButton_() {
  const navigate = useNavigate();

  function onClick() {
    navigate("/book/details");
  }

  return (
    <Button primary size="large" onClick={onClick}>
      Get Instant Quotes
    </Button>
  );
}

function EmptyStateScreen_({
  showDeliveredShipmentsLink,
  isUser = false,
}: {
  showDeliveredShipmentsLink?: boolean | undefined;
  isUser?: boolean | undefined;
}) {
  return (
    <Stack gap={8} align="center">
      <Stack gap={0} align="center">
        {/* Replica of the legacy Cargo Heading1 (28px bold). */}
        <Heading level={1}>
          {isUser
            ? "You don't have any recent shipments with this user"
            : "You don't have any recent shipments"}
        </Heading>
        <T size="base" color="secondary">
          Shipments that are relevant to you today will show here. Get quotes to
          start booking your next shipment.{" "}
          {showDeliveredShipmentsLink && (
            <>
              You can view all your delivered shipments{" "}
              <Link href="/view-all-shipments?shipmentState=delivered&sort=DeliveryDate">
                here
              </Link>
            </>
          )}
        </T>
      </Stack>
      <AddShipmentButton_ />
    </Stack>
  );
}

const DashboardScreen: React.FC = () => {
  const shipmentsService = useShipmentService();
  const { bookerNameFor } = useBookerNames();
  const [bookedShipments, setBookedShipments] = useState<Array<Shipment>>([]);
  const [showPhotoUploadReminder, setShowPhotoUploadReminder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const { isDismissed: photoReminderDismissed, dismiss: dismissPhotoReminder } =
    useDashboardPhotoUploadReminderDismissed();

  const {
    usersLoading,
    element: userDropdownElement,
    filterByUserId,
    filterByBranchId,
  } = useDashboardUserSelect(UserSelectType.Booking);

  // A filter change starts a new load while the previous one may still be in
  // flight; only the latest may decide what the page shows
  const latestLoad = useRef(0);

  async function loadShipments() {
    const thisLoad = ++latestLoad.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await shipmentsService.getShipments();
      if (thisLoad !== latestLoad.current) return;
      setBookedShipments(response.booked);
      setShowPhotoUploadReminder(response.showPhotoUploadReminder);
    } catch (e) {
      if (thisLoad !== latestLoad.current) return;
      console.warn("Error loading shipments:", { e });
      setLoadFailed(true);
    } finally {
      if (thisLoad === latestLoad.current) setLoading(false);
    }
  }

  const filteredShipments = bookedShipments
    .filter(function (shipment) {
      if (shipment.bookedBy === undefined) {
        return true;
      }

      if (filterByUserId === undefined || filterByUserId === "") {
        return true;
      }

      return shipment.bookedBy === filterByUserId;
    })
    .filter(function (shipment) {
      if (filterByBranchId === undefined || filterByBranchId === "") {
        return true;
      }

      if (filterByBranchId === BranchFilterOptionType.Uncoded) {
        return shipment.branchId === undefined;
      }

      return shipment.branchId === filterByBranchId;
    });

  useEffect(
    function () {
      loadShipments();
    },

    [filterByUserId],
  );

  if (loading) {
    return <PageLoadingSpinner />;
  }

  function isDeliveringoTodayOrTomorrow(shipment: Shipment) {
    if (shipment.expectedDeliveryDate === undefined) {
      return false;
    }

    const today = moment().startOf("day");
    const tomorrow = moment().startOf("day").add(1, "day");

    const m = moment(shipment.expectedDeliveryDate);

    return m.isSame(today) || m.isSame(tomorrow);
  }

  function wasShipmentDeliveredWithinLast3Days(shipment: Shipment) {
    const actualDeliveryDate = shipment.actualDeliveryDate;
    if (actualDeliveryDate === undefined) {
      return false;
    }

    const threeDaysAgo = moment().startOf("day").subtract(3, "days");
    if (moment(actualDeliveryDate).isSameOrAfter(threeDaysAgo)) {
      return true;
    } else {
      return false;
    }
  }

  const shipmentsFinishingUp = filteredShipments.filter(
    (s) => s.shipmentState === ShipmentState.BookingFailed,
  );

  const shipmentsOnHold = filteredShipments.filter(
    (s) => s.shipmentState === ShipmentState.OnHold,
  );

  const shipmentsPickingUpSoon = filteredShipments.filter(
    (s) => s.shipmentState === ShipmentState.BookingConfirmed,
  );

  const shipmentsInTransit = filteredShipments.filter(
    (s) =>
      s.shipmentState === ShipmentState.InTransit &&
      !isDeliveringoTodayOrTomorrow(s),
  );

  const shipmentsDeliveringTodayOrTomorrow = filteredShipments.filter(
    (s) =>
      s.shipmentState === ShipmentState.InTransit &&
      isDeliveringoTodayOrTomorrow(s),
  );

  const shipmentsRecentlyDelivered = filteredShipments.filter(
    (s) =>
      s.shipmentState === ShipmentState.Delivered &&
      wasShipmentDeliveredWithinLast3Days(s),
  );

  const noShipments = bookedShipments.length === 0;

  const showDeliveredShipmentsLink = filteredShipments.length > 0;

  const showUncodedOption = bookedShipments.some(
    (s) => s.branchId === undefined,
  );

  return (
    <>
      <FullWidthLayout
        header={<>Active Shipments</>}
        microcopy={
          <>
            Here are the shipments that are most relevant for today. You can get
            quotes for your next shipment <Link href="/book/details">here</Link>
            .
          </>
        }
        loading={loading || usersLoading}
        showEmptyState={noShipments}
        onRetryFailedLoad={loadFailed ? loadShipments : undefined}
        emptyState={
          <EmptyStateScreen_
            showDeliveredShipmentsLink={showDeliveredShipmentsLink}
          />
        }
        rightContent={userDropdownElement(showUncodedOption)}
        content={
          filteredShipments.length > 0 ? (
            <Stack gap={4}>
              {showPhotoUploadReminder && !photoReminderDismissed && (
                <DashboardPhotoUploadReminderBanner
                  onDismiss={dismissPhotoReminder}
                />
              )}
              <SectionForShipmentState_
                sectionTitle={"Finishing up"}
                subtitle={`Preparation of documents is being finalized`}
                shipments={shipmentsFinishingUp}
                bookerNameFor={bookerNameFor}
              />
              <SectionForShipmentState_
                sectionTitle={"On Hold"}
                subtitle={undefined}
                shipments={shipmentsOnHold}
                bookerNameFor={bookerNameFor}
              />
              <SectionForShipmentState_
                sectionTitle={"Picking up soon"}
                subtitle={undefined}
                shipments={shipmentsPickingUpSoon}
                bookerNameFor={bookerNameFor}
              />
              <SectionForShipmentState_
                sectionTitle={"In Transit"}
                subtitle={undefined}
                shipments={shipmentsInTransit}
                bookerNameFor={bookerNameFor}
              />
              <SectionForShipmentState_
                sectionTitle={"Delivering Today or Tomorrow"}
                subtitle={undefined}
                shipments={shipmentsDeliveringTodayOrTomorrow}
                bookerNameFor={bookerNameFor}
              />
              <SectionForShipmentState_
                sectionTitle={"Recently Delivered"}
                subtitle={
                  <>
                    These shipments were delivered in the last 3 days. You can
                    view all your delivered shipments{" "}
                    <Link href="/view-all-shipments?shipmentState=delivered&sort=DeliveryDate">
                      here
                    </Link>
                  </>
                }
                shipments={shipmentsRecentlyDelivered}
                bookerNameFor={bookerNameFor}
              />
            </Stack>
          ) : (
            <Center minHeight="400px">
              <EmptyStateScreen_
                showDeliveredShipmentsLink={showDeliveredShipmentsLink}
                isUser={true}
              />
            </Center>
          )
        }
      />
    </>
  );
};

export default DashboardScreen;
